import * as React from "react";
import { observer, useLocalObservable } from "mobx-react-lite";
import {
	AuthenticationStore,
	PreSimulationStore,
	FintechRegisterStore,
	ValidationOTPStore,
	AnticipationStore,
	PaymentInfo,
} from "../stores";
import { PasswordStore } from "../stores/PasswordStore";
import { ChakraProvider } from "@chakra-ui/react";
import { theme } from "../theme";
import { FraudAnalysisStore } from "../stores/FraudAnalysis";

interface IProps {
	children: React.ReactNode;
}

interface IGlobalStores {
	authenticationStore: AuthenticationStore;
	preSimulationStore: PreSimulationStore;
	anticipationStore: AnticipationStore;
	fintechRegisterStore: FintechRegisterStore;
	validationOTPStore: ValidationOTPStore;
	passwordStore: PasswordStore;
	paymentInfo: PaymentInfo;
	fraudAnalysisStore: FraudAnalysisStore;
}

const GlobalStoreContext = React.createContext<IGlobalStores | null>(null);

export const GlobalStoreProvider: React.FC<IProps> = observer((props) => {
	const { children } = props;

	const authenticationStore = useLocalObservable(() => new AuthenticationStore());
	const fintechRegisterStore = useLocalObservable(() => new FintechRegisterStore(authenticationStore));
	const preSimulationStore = useLocalObservable(() => new PreSimulationStore());
	const anticipationStore = useLocalObservable(() => new AnticipationStore(authenticationStore));
	const validationOTPStore = useLocalObservable(() => new ValidationOTPStore(authenticationStore, fintechRegisterStore));
	const passwordStore = useLocalObservable(
		() => new PasswordStore(authenticationStore, fintechRegisterStore, validationOTPStore),
	);
	const paymentInfo = useLocalObservable(() => new PaymentInfo());
	const fraudAnalysisStore = useLocalObservable(() => new FraudAnalysisStore(authenticationStore));

	return (
		<GlobalStoreContext.Provider
			value={{
				authenticationStore,
				preSimulationStore,
				anticipationStore,
				fintechRegisterStore,
				validationOTPStore,
				passwordStore,
				paymentInfo,
				fraudAnalysisStore,
			}}
		>
			<ChakraProvider theme={theme}>{children}</ChakraProvider>
		</GlobalStoreContext.Provider>
	);
});

export const useGlobalStore = (): IGlobalStores => {
	const stores = React.useContext(GlobalStoreContext);

	if (!stores) {
		throw new Error("Can't access store outside it's context");
	}

	return stores;
};
